import { useTranslation } from 'i18n';
import { Box, Button, Link, Page, Stack, Typography } from 'ui';
import { WarningTwoTone } from 'ui/icons';
import { getErrorCategory } from '../../utils/getErrorCategory/getErrorCategory';
import { isError } from '../../utils/isError/isError';

const SUPPORT_EMAIL = 'support@safi.co';
const DEFAULT_HOME_HREF = '/quality-control';

export const ErrorDetails = ({
  error,
  homeHref = DEFAULT_HOME_HREF,
  inline = false,
  logoSrc,
  onRetry,
}: {
  error: unknown;
  homeHref?: string;
  // Render just the message/actions without the full-page shell, for embedding inside an
  // existing page (accordion rows, drawers, sections). Route-level callers keep the default.
  inline?: boolean;
  logoSrc?: string;
  onRetry?: () => void;
}) => {
  const { t } = useTranslation('features');
  const category = getErrorCategory(error);

  const copy = {
    forbidden: { description: t('error.forbidden.description'), title: t('error.forbidden.title') },
    // Show the error's own message for generic errors (callers pass meaningful
    // messages, e.g. new Error('...')); fall back to the default copy otherwise.
    generic: {
      description: isError(error) ? error.message : t('error.generic.description'),
      title: t('error.generic.title'),
    },
    network: { description: t('error.network.description'), title: t('error.network.title') },
    notFound: { description: t('error.notFound.description'), title: t('error.notFound.title') },
    server: { description: t('error.server.description'), title: t('error.server.title') },
  }[category];

  const showHomepage = category === 'notFound' || category === 'forbidden';

  const supportDetail = isError(error) ? error.message : copy.title;
  // Only origin + pathname — avoid leaking query/hash values into the support email.
  const supportUrl = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : '';
  const supportSubject = encodeURIComponent(t('error.support.subject'));
  const supportBody = encodeURIComponent(
    t('error.support.body', { values: { detail: supportDetail, url: supportUrl } })
  );
  const supportHref = `mailto:${SUPPORT_EMAIL}?subject=${supportSubject}&body=${supportBody}`;

  const content = (
    <Stack
      component="section"
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        mt: 4,
      }}
    >
      <Stack
        spacing={4}
        sx={{
          alignItems: 'center',
          maxWidth: 420,
          textAlign: 'center',
          width: '100%',
        }}
      >
        {logoSrc ? (
          <Box component="img" src={logoSrc} alt="Safi" sx={{ height: 28, width: 'auto' }} />
        ) : (
          <WarningTwoTone sx={{ color: 'text.secondary', fontSize: 40 }} />
        )}

        <Stack spacing={1.5} sx={{ alignItems: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {copy.title}
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {copy.description}
          </Typography>
        </Stack>

        <Stack spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
          {showHomepage ? (
            <Button variant="primary" size="large" component="a" href={homeHref} fullWidth>
              {t('error.actions.goToHomepage')}
            </Button>
          ) : (
            <Button variant="primary" size="large" fullWidth onClick={onRetry ?? (() => window.location.reload())}>
              {t('error.actions.tryAgain')}
            </Button>
          )}
          <Link component="a" href={supportHref}>
            {t('error.actions.contactSupport')}
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );

  return inline ? content : <Page>{content}</Page>;
};
